import { useCallback, useMemo, useState, useRef } from 'react';
import { UseFormWatch } from 'react-hook-form';
import { objectToCamel } from 'ts-case-convert';

import { useFetchRiskFactor } from './useFetchRiskFactor';

import {
    AssetSectionFields,
    BorrowAmountFields,
    RiskFactorFields,
} from '@/features/Modals/ui/BorrowModal/fields';
import { subtractFixedDecimals } from '@/shared/utils/helpers';
import { LeverageStrategy } from '@/shared/validations/borrowModal.schema';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { Agreement } from '@/shared/api/borrow';

type RiskFactorData = {
    riskFactor: string;
    stressTestedValue: string;
    collateralStressTested: string;
    marketValue: string;
};

type UseGetRiskFactorProps = {
    agreement: Agreement;
    assetSymbol: string;
    agreementAddress: string;
    fieldWatch: UseFormWatch<any>;
};

const DEBOUNCE_INTERVAL = 100;

export const useGetRiskFactor = ({
    agreement,
    assetSymbol,
    agreementAddress,
    fieldWatch,
}: UseGetRiskFactorProps) => {
    const [data, setData] = useState<RiskFactorData | null>(null);

    const selectedAssets = fieldWatch(AssetSectionFields.SELECTED_ASSETS);
    const borrowAmount = fieldWatch(BorrowAmountFields.ASSET_AMOUNT);
    const multiplier = fieldWatch(RiskFactorFields.MULTIPLIER);
    const strategy = fieldWatch(RiskFactorFields.STRATEGY);

    const prevBorrowAmount = useRef<string>();

    const { fetchRiskFactor } = useFetchRiskFactor({
        agreement,
        assetSymbol,
        agreementAddress,
    });

    const isLeverageWithPercentStrategy = useMemo(
        () => strategy === LeverageStrategy.PREDEFINED_WITH_PERCENT,
        [strategy],
    );

    const leverageAmount = useMemo(() => {
        if (!borrowAmount) {
            return '0';
        }

        if (isLeverageWithPercentStrategy) {
            if (!prevBorrowAmount.current) {
                prevBorrowAmount.current = '0';
            }

            return prevBorrowAmount.current;
        }

        prevBorrowAmount.current = borrowAmount.toString();

        return borrowAmount.toString();
    }, [borrowAmount, isLeverageWithPercentStrategy]);

    const getRiskFactorFromBE = useCallback(async () => {
        if (!selectedAssets || (isLeverageWithPercentStrategy && data?.riskFactor)) {
            return;
        }

        const response = await fetchRiskFactor({
            selectedAssets,
            borrowAmount: leverageAmount,
        });

        if (!response) {
            return;
        }

        const result = objectToCamel(response);

        setData({
            riskFactor: result?.riskFactor,
            collateralStressTested: subtractFixedDecimals(
                result?.stressTestedValue as string,
                leverageAmount,
                agreement.leverage.decimals,
            ),
            stressTestedValue: result?.stressTestedValue,
            marketValue: result?.marketValue,
        } as unknown as RiskFactorData);
    }, [leverageAmount, JSON.stringify(selectedAssets)]);

    const collateralMarketValue = useMemo(() => {
        if (!data?.marketValue) return null;

        let marketValue = data.marketValue;

        if (marketValue.includes('e')) {
            marketValue = Number(marketValue).toFixed(agreement.leverage.decimals);
        }

        return subtractFixedDecimals(
            marketValue,
            leverageAmount || '0',
            agreement.leverage.decimals,
        );
    }, [data?.marketValue, leverageAmount, agreement.leverage.decimals]);

    const desiredLeverageAmount = useMemo(() => {
        return Number(collateralMarketValue) * Number(multiplier);
    }, [collateralMarketValue, multiplier]);

    const liquidationFee = useMemo(() => {
        return 1 - Number(agreement?.liquidationFee) / 100;
    }, [agreement?.liquidationFee]);

    const calculatedRiskFactor = useMemo(() => {
        if (
            !data?.collateralStressTested ||
            !desiredLeverageAmount ||
            !liquidationFee ||
            !leverageAmount
        ) {
            return null;
        }

        if (isLeverageWithPercentStrategy) {
            const result =
                ((Number(data?.collateralStressTested) + Number(desiredLeverageAmount)) *
                    liquidationFee) /
                Number(desiredLeverageAmount);

            return result.toString();
        }

        if (leverageAmount === '0') {
            return 'N/A';
        }

        const result =
            ((Number(data?.collateralStressTested) + Number(leverageAmount)) * liquidationFee) /
            Number(leverageAmount);

        return result.toString();
    }, [
        data?.collateralStressTested,
        desiredLeverageAmount,
        liquidationFee,
        leverageAmount,
        multiplier,
        isLeverageWithPercentStrategy,
        agreement.leverage.decimals,
    ]);

    useDebounce(getRiskFactorFromBE, DEBOUNCE_INTERVAL, [
        leverageAmount,
        JSON.stringify(selectedAssets),
    ]);

    return {
        stressTestedValue: data?.stressTestedValue || null,
        riskFactor: calculatedRiskFactor || null,
        desiredLeverageAmount: desiredLeverageAmount,
        collateralMarketValue: collateralMarketValue,
        collateralStressTested: data?.collateralStressTested || null,
        marketValue: data?.marketValue,
    };
};
